import { fbt } from 'fbtee'
import { Link as LinkIcon, Share2, CalendarClock, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { Button } from '../../components/ui/button'
import { Section } from '../../components/Section'
import { EventAttendanceMenu } from '../../components/EventAttendanceMenu'
import { useCoarsePointer } from '../../hooks/useCoarsePointer'
import { copyEventLink, nativeShareEvent } from '../../lib/share'
import { getGradientPair, scheduleSetPath } from '../../lib/utils'
import { track } from '../../lib/analytics'
import { InfoView } from './InfoView'
import { PeopleSection } from './PeopleSection'
import { GroupsSection } from './GroupsSection'
import { PregamesSection } from './PregamesSection'
import { TicketsSection } from './Tickets/TicketsSection'
import { ticketsHaveContent } from './Tickets/ticketsHaveContent'
import { isEventActive } from '../../lib/formatDate'
import { getNow } from '../../lib/clock'
import { FeedSection } from './FeedSection'
import { LineupView } from './LineupView'
import { ReportEventButton } from './ReportEventButton'
import { focalPosition } from '../../lib/focalPosition'
import type { Event, Venue } from '../../types'
import type { EventPageData } from './useEventPageData'

/**
 * A party is one night with a handful of artists, so everything that matters —
 * what it is, who's coming, who's playing — is stacked on one page instead of
 * behind the festival's pill tabs. Set times, when a party even has them, live
 * on the `/schedule` sub-route.
 */
export function PartyHome({
  event,
  venue,
  dateRange,
  data,
  artistUserIds,
}: {
  event: Event
  venue?: Venue | null
  dateRange: string
  data: EventPageData
  artistUserIds: Record<string, string[]>
}) {
  const isMobile = useCoarsePointer()
  const navigate = useTransitionNavigate()
  const [c1, c2] = getGradientPair(event.id)

  // Power-user tools: up top only once they hold something for the viewer, otherwise at the bottom.
  const canEdit = !event.cancelled && isEventActive(event, getNow())
  const ticketsActive = !data.tickets.loading && ticketsHaveContent(data.tickets, data.pregameUserId ?? '', canEdit)
  const pregamesActive = !data.pregames.loading && (!!data.pregames.mine || data.pregames.invited.length > 0)
  const ticketsSection = (
    <TicketsSection
      event={event}
      tickets={data.tickets}
      currentUserId={data.pregameUserId}
      groups={data.groups}
      friendIds={data.friendIds}
      profiles={data.mergedUserProfiles}
      profilesLoading={data.profilesLoading}
      goingUserIds={data.goingUserIds}
      interestedUserIds={data.interestedUserIds}
    />
  )
  const pregamesSection = (
    <PregamesSection event={event} pregames={data.pregames} currentUserId={data.pregameUserId} groups={data.groups} />
  )

  return (
    <>
      <div className="-mt-4 -mx-4">
        <div className="relative h-48 w-full overflow-hidden">
          {event.imageUrl ? (
            <img
              src={event.imageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: focalPosition(event, 'center') }}
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{ backgroundImage: `linear-gradient(135deg, ${c1}, ${c2})` }}
            />
          )}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-background" />
          <div className="absolute top-2 right-2 flex gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-full bg-black/40 backdrop-blur-sm text-on-image hover:bg-black/60 hover:text-on-image"
              onClick={() => copyEventLink(event.id)}
              aria-label={fbt('Copy event link', 'Accessible label for the copy event link button')}
            >
              <LinkIcon className="size-4" />
            </Button>
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8 rounded-full bg-black/40 backdrop-blur-sm text-on-image hover:bg-black/60 hover:text-on-image"
                onClick={() => nativeShareEvent(event.id, event.name)}
                aria-label={fbt('Share event', 'Accessible label for the share event button')}
              >
                <Share2 className="size-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="relative z-10 flex justify-end px-4 -mt-7">
          <EventAttendanceMenu
            variant="circle"
            status={data.eventStatus}
            loading={data.eventStatusLoading}
            onSetStatus={data.handleSetInterest}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6 pt-2">
        <InfoView event={event} dateRange={dateRange} venue={venue} showReport={false} />

        {ticketsActive && ticketsSection}
        {pregamesActive && pregamesSection}

        <PeopleSection
          loading={data.peopleLoading}
          profilesLoading={data.profilesLoading}
          linkedGroups={data.linkedGroups}
          friendIds={data.friendIds}
          goingUserIds={data.goingUserIds}
          interestedUserIds={data.interestedUserIds}
          userProfiles={data.mergedUserProfiles}
          currentUserId={data.currentUserId}
          pregameGoers={data.pregameGoers}
          ticketHolderIds={data.tickets.peopleHolderIds}
          ticketPendingFrom={data.tickets.peoplePendingFrom}
        />

        {(data.lineupLoading || data.lineup.length > 0) && (
          <Section
            title={fbt('Lineup', 'Section heading for the artist lineup on a party page')}
            action={
              data.hasSchedule ? (
                <Link
                  to={`/events/${event.id}/schedule`}
                  onClick={() => track('view_tab_switch', { tab: 'schedule' })}
                  className="flex items-center gap-1 text-xs font-medium text-primary"
                >
                  <CalendarClock className="size-3.5" />
                  {fbt('Set times', 'Link on a party page that opens the set-times schedule')}
                  <ChevronRight className="size-3.5" />
                </Link>
              ) : undefined
            }
          >
            <LineupView
              lineup={data.lineup}
              lineupLoading={data.lineupLoading}
              lineupMode={event.lineupMode ?? 'day'}
              stages={data.stages}
              artists={data.artists}
              interestedArtistIds={data.interestedArtistIds}
              onToggleArtist={data.guardedToggleArtistInterest}
              onMarkArtists={data.guardedMarkArtists}
              followHintIds={data.followHintIds}
              followCtaArtistIds={data.followCtaArtistIds}
              onAddFollowed={data.addFollowedArtists}
              artistUserIds={artistUserIds}
              userProfiles={data.mergedUserProfiles}
              currentUserId={data.currentUserId}
              defaultDay={data.defaultDay}
              feed={data.setFeed}
              showSearch={data.lineup.length > 8}
              tiered={false}
            />
          </Section>
        )}

        <GroupsSection
          eventId={event.id}
          isFestival={false}
          groups={data.groups}
          linkedGroups={data.linkedGroups}
          goingUserIds={data.goingUserIds}
          interestedUserIds={data.interestedUserIds}
          profiles={data.mergedUserProfiles}
          isGuest={data.isGuest}
          onUnlink={data.unlinkGroup}
          canUnlink={data.canUnlinkGroup}
          onCreateGroup={data.handleCreateGroup}
        />

        <FeedSection
          feed={data.setFeed}
          artists={data.artists}
          sets={data.allSets}
          userProfiles={data.mergedUserProfiles}
          currentUserId={data.currentUserId}
          hasSchedule={data.hasSchedule}
          onNavigateToSet={set => {
            track('view_tab_switch', { tab: 'schedule' })
            navigate(scheduleSetPath(event.id, set.id))
          }}
          eventId={event.id}
        />

        {!ticketsActive && ticketsSection}
        {!pregamesActive && pregamesSection}

        <ReportEventButton eventId={event.id} />
      </div>
    </>
  )
}
